import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { useCallback, useMemo, useRef, useState } from 'react';
import { DateRange, rangeConfig } from 'src/components/DateRange';
import LoadingIndicator from 'src/components/LoadingIndicator';
import NavTab from 'src/components/NavTab';
import NoTransfers from 'src/components/NoTransfers';
import { TablePaginationBtns } from 'src/components/PaginationBtns';
import { ShipmentCost } from 'src/components/ShipmentBanner';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Icon,
  Input,
  Nav,
  Row,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  UncontrolledTooltip,
  useApp,
  useScreenSize,
  useUrlState
} from 'straightline-ui';
import { formatMoney } from 'straightline-utils/accounting';
import { formatDate } from 'straightline-utils/time';

function truncate(text) {
  if (!text) {
    return '';
  }

  if (text.length <= 30) {
    return text;
  }

  return text.substr(0, 30) + '\u2026';
}

const statusColor = {
  PAID: 'soft-success',
  OPEN: 'soft-primary',
  VOID: 'soft-secondary',
  UNCOLLECTIBLE: 'soft-danger'
};

const statusOptions = [
  { label: 'All Payments', value: 'all' },
  { label: 'Paid', value: 'PAID' },
  { label: 'Open', value: 'OPEN' }
];

export default function ShipmentTransfers() {
  const app = useApp();
  const queryState = useUrlState();
  const query = queryState.getInState('query', {
    $sort: { created_at: -1 }
  });

  const {
    result: shipmentTransfers,
    isLoading,
    isError
  } = app.service('api/shipment-transfers').useFind(
    {
      $include: ['shipment'],
      query
    },
    { keepPreviousData: true }
  );

  if (isLoading || isError) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (!shipmentTransfers.total === 0) {
    return <NoTransfers />;
  }

  return (
    <Container fluid className="pb-5">
      <Row className="justify-content-center">
        <Col>
          <TransfersHeader queryState={queryState} query={query} />
          <Card>
            <SearchBar
              queryState={queryState}
              total={shipmentTransfers.total}
            />
            <div className="table-responsive">
              <Table className="table table-nowrap card-table table-hover">
                <TableHead>
                  <TableRow>
                    <TableHeader className="py-3">Status</TableHeader>
                    <TableHeader className="py-3">Shipment</TableHeader>
                    <TableHeader className="py-3">Pickup</TableHeader>
                    <TableHeader className="py-3">Delivery</TableHeader>
                    <TableHeader className="py-3">Shipper</TableHeader>
                    <TableHeader className="py-3">Amount</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody className="list">
                  {shipmentTransfers.data.map((shipmentTransfer) => {
                    const shipment = shipmentTransfer.shipment;
                    if (!shipment) {
                      return (
                        <TableRow
                          key={shipmentTransfer.id}
                          className="pointer"
                          onClick={() => {
                            window.open(shipmentTransfer.secure_url, '_blank');
                          }}
                        >
                          <TableData>
                            <Badge
                              color={statusColor[shipmentTransfer.status]}
                              style={{ padding: 8.5 }}
                            >
                              {shipmentTransfer.status.charAt(0).toUpperCase() +
                                shipmentTransfer.status.slice(1).toLowerCase()}
                            </Badge>
                          </TableData>
                          <TableData>
                            <div>
                              <span className="text-muted">SL#: </span>
                              {shipmentTransfer.shipment_id}
                            </div>
                            <div>
                              <span className="text-muted">REF#: </span>
                              <span className="text-muted">NA</span>
                            </div>
                          </TableData>
                          <TableData colSpan="3">
                            Shipment information not available for Load #
                            {shipmentTransfer.shipment_id}. You may have
                            received <br /> a payment for a load that is not
                            assigned to you.
                          </TableData>
                          <TableData>
                            <p className="mb-0">
                              {formatMoney(shipmentTransfer.due_amount || 0)}{' '}
                              <span className="small text-muted">due</span>
                            </p>
                            <p className="mb-0">
                              {formatMoney(
                                shipmentTransfer.paid_amount +
                                  shipmentTransfer.fee_amount
                              )}{' '}
                              <span className="small text-muted">paid</span>
                            </p>
                          </TableData>
                        </TableRow>
                      );
                    }
                    return (
                      <TableRow
                        key={shipmentTransfer.id}
                        to={`/shipments/${shipment.id}?tab=rate-confirmation`}
                      >
                        <TableData style={{ width: '1%' }}>
                          <Badge
                            color={statusColor[shipmentTransfer.status]}
                            style={{ padding: 8.5 }}
                          >
                            {shipmentTransfer.status.charAt(0).toUpperCase() +
                              shipmentTransfer.status.slice(1).toLowerCase()}
                          </Badge>
                        </TableData>
                        <TableData>
                          <div>
                            <span className="text-muted">SL#: </span>
                            {shipment.id}
                          </div>
                          <div>
                            <span className="text-muted">REF#: </span>
                            {truncate(shipment.reference_number) || (
                              <span className="text-muted">NA</span>
                            )}
                          </div>
                        </TableData>
                        <TableData className="" style={{ width: '1%' }}>
                          <div>
                            {`${shipment.pickup_address.city}, ${shipment.pickup_address.state}`}
                            {shipment.pickup_address.name && (
                              <span
                                id={`pickup-name-${shipment.id}`}
                                className="ml-1"
                              >
                                <Icon type="info" className="text-secondary" />
                                <UncontrolledTooltip
                                  target={`pickup-name-${shipment.id}`}
                                >
                                  {shipment.pickup_address.name}
                                </UncontrolledTooltip>
                              </span>
                            )}
                          </div>
                          <div>
                            {formatDate(shipment.pickup_date, 'LLL do, yyyy')}
                          </div>
                        </TableData>
                        <TableData
                          className=""
                          style={{ width: '1%', position: 'relative' }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              left: -12,
                              top: 21
                            }}
                          >
                            <Icon
                              type="arrow-right text-success"
                              style={{ fontSize: 25 }}
                            />
                          </div>
                          <div>
                            {`${shipment.delivery_address.city}, ${shipment.delivery_address.state}`}
                            {shipment.delivery_address.name && (
                              <span
                                id={`delivery-name-${shipment.id}`}
                                className="ml-1"
                              >
                                <Icon type="info" className="text-secondary" />
                                <UncontrolledTooltip
                                  target={`delivery-name-${shipment.id}`}
                                >
                                  {shipment.delivery_address.name}
                                </UncontrolledTooltip>
                              </span>
                            )}
                          </div>
                          <div>
                            {formatDate(shipment.due_date, 'LLL do, yyyy')}
                          </div>
                        </TableData>
                        <TableData>
                          <div>{shipment.shipper.name}</div>
                          <div>
                            <ShipmentCost shipment={shipment} />
                          </div>
                        </TableData>
                        <TableData>
                          <p className="mb-0">
                            {formatMoney(shipmentTransfer.due_amount)}{' '}
                            <span className="small text-muted">due</span>
                          </p>
                          <p className="mb-0">
                            {formatMoney(
                              shipmentTransfer.paid_amount +
                                shipmentTransfer.fee_amount
                            )}{' '}
                            <span className="small text-muted">paid</span>
                          </p>
                        </TableData>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

function TransfersHeader({ queryState, query }) {
  const app = useApp();
  const { isXs, isSm, isMd, md } = useScreenSize();
  const tab = queryState.getInState('query.status', 'all');

  const handleTabClick = async (status) => {
    const { ...newQuery } = queryState.getInState('query', {});

    if (status === 'all') {
      delete newQuery.status;
    } else {
      newQuery.status = status;
    }

    delete newQuery.$skip;
    delete newQuery.$limit;

    queryState.setInState('query', newQuery);
  };

  const LabelTag = isXs || isSm || isMd ? 'h3' : 'h1';
  const labelStyle = {
    minWidth: md ? '80px' : ''
  };

  const statsQuery = { ...query };
  delete statsQuery.$skip;
  delete statsQuery.$limit;

  const { result: stats } = app
    .service('api/shipment-transfer-stats')
    .useFind({ query: statsQuery }, { keepPreviousData: true });

  const totals = stats?.total;

  return (
    <div className="header pt-md-2">
      <div className="header-body">
        <Row className="justify-content-between mb-n3">
          <Col xs="auto" className="mb-3">
            <h6 className="header-pretitle">Overview</h6>
            <LabelTag className="header-title">Payments</LabelTag>
          </Col>
          <Col className="col-auto mb-3">
            <Row className="mb-n2">
              <Col xs="auto" className="text-center mb-2">
                <h6 className="header-pretitle">Total Billed</h6>
                <LabelTag className="header-title" style={labelStyle}>
                  {totals && (
                    <span
                      className="animated fadeIn"
                      key={totals.due_amount.sum}
                    >
                      {formatMoney(totals.due_amount.sum, {
                        precision: 0
                      })}
                    </span>
                  )}
                </LabelTag>
              </Col>
              <Col xs="auto" className="text-center mb-2">
                <h6 className="header-pretitle">Total Fees</h6>
                <LabelTag className="header-title" style={labelStyle}>
                  {totals && (
                    <span
                      className="animated fadeIn"
                      key={totals.fee_amount.sum}
                    >
                      {formatMoney(totals.fee_amount.sum, {
                        precision: 0
                      })}
                    </span>
                  )}
                </LabelTag>
              </Col>
              <Col xs="auto" className="text-center mb-2">
                <h6 className="header-pretitle">Total Paid</h6>
                <LabelTag className="header-title" style={labelStyle}>
                  {totals && (
                    <span
                      className="animated fadeIn"
                      key={totals.paid_amount.sum + totals.fee_amount.sum}
                    >
                      {formatMoney(
                        totals.paid_amount.sum + totals.fee_amount.sum,
                        {
                          precision: 0
                        }
                      )}
                    </span>
                  )}
                </LabelTag>
              </Col>
              <Col xs="auto" className="text-center mb-2">
                <h6 className="header-pretitle">Total Due</h6>
                <LabelTag className="header-title" style={labelStyle}>
                  {totals && (
                    <span
                      className="animated fadeIn"
                      key={totals.outstanding_amount.sum}
                    >
                      {totals.outstanding_amount.sum > 0
                        ? formatMoney(totals.outstanding_amount.sum, {
                            precision: 0
                          })
                        : formatMoney(0, { precision: 0 })}
                    </span>
                  )}
                </LabelTag>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="align-items-center">
          <Col>
            <Nav tabs className="nav-overflow header-tabs">
              {statusOptions.map((option) => (
                <NavTab
                  key={option.value}
                  tab={option.value}
                  onClick={handleTabClick}
                  activeTab={tab}
                >
                  {option.label}
                </NavTab>
              ))}
            </Nav>
          </Col>
        </Row>
      </div>
    </div>
  );
}

function SearchBar({ queryState, total }) {
  const { isXs, isSm, isMd, isLg, isXl } = useScreenSize();
  const rangeWrapper = useRef(null);

  const { getInState, opInState, delInState, setInState } = queryState;
  const query = getInState('query', {});
  const $search = getInState('query.$search', '');
  const [text, setText] = useState($search);

  // eslint-disable-next-line
  const debouncedHandleChange = useCallback(
    debounce((value) => {
      opInState((newState) => {
        newState.del('query.$skip');

        const val = value && value.trim ? value.trim() : value;

        if (val) {
          newState.set('query.$search', val);
        } else {
          newState.del('query.$search');
        }

        return newState;
      });
    }, 300),
    []
  );

  const handleSearchChange = ({ target: { value } }) => {
    setText(value);
    debouncedHandleChange(value);
  };

  const handleSearchClear = () => {
    setText('');
    delInState('query.$search', 'query.$skip');
  };

  const ranges = useMemo(
    () => rangeConfig(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formatDate(new Date(), 'dateOnly')]
  );

  const handleDateChange = ({ target: { value } }) => {
    opInState((newState) => {
      newState.del('query.$skip');

      if (!value || !value.length || !value[0] || !value[1]) {
        newState.del('query.created_at');
        return newState;
      }

      newState.set('query.created_at', {
        $between: [
          formatDate(value[0], 'dateOnly'),
          formatDate(value[1], 'dateOnly')
        ]
      });

      return newState;
    });
  };

  return (
    <div className="px-3" ref={rangeWrapper}>
      <Row className="align-items-center">
        <Col
          md="12"
          lg
          className={classNames('py-2', {
            'border-right': isLg || isXl,
            'border-bottom': isXs || isSm || isMd
          })}
        >
          <Row className="align-items-center">
            <Col>
              <Row className="align-items-center">
                <Col className="col-auto pr-0">
                  <Icon type="search" className="text-muted" />
                </Col>
                <Col>
                  <Input
                    value={text}
                    onChange={handleSearchChange}
                    className="form-control-flush"
                    placeholder="Search"
                  />
                </Col>
                {text && (
                  <Col className="col-auto p-0">
                    <Button
                      className="text-muted"
                      color="default"
                      type="button"
                      size="sm"
                      onClick={handleSearchClear}
                    >
                      <Icon type="x" />
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
        <Col
          md="12"
          lg="auto"
          className="py-3 py-lg-2 d-flex align-items-center"
        >
          <Row noGutters className="mb-n1">
            <Col xs="auto" className="mr-2 mb-1">
              <TablePaginationBtns
                total={total}
                limit={10}
                skip={query.$skip || 0}
                setSkip={($skip) => setInState('query.$skip', $skip)}
                color="light"
              />
            </Col>
            <Col xs="auto" className="mb-1">
              <DateRange
                right
                placeholder="Payment Date"
                size="sm"
                value={query.created_at?.$between || []}
                ranges={ranges}
                onChange={handleDateChange}
                wrapper={rangeWrapper}
              />
            </Col>
            {/* <Col xs="auto" className="mb-1">
              <DownloadButton query={query} size="sm" color="light">
                <Icon type="download" className="mr-1" />
                Download
              </DownloadButton>
            </Col> */}
          </Row>
        </Col>
      </Row>
    </div>
  );
}
